import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { stockApi } from '../../api/client';
import { StockItem, StockEntry } from '../../types';
import {
  Plus, X, AlertTriangle, Package, TrendingDown, DollarSign,
  Save, Search, Filter, PackagePlus,
} from 'lucide-react';

// ─── Design tokens ───────────────────────────────────────────────────────────

const C = {
  bg: '#0a0a0f',
  card: '#12121a',
  border: '#1e1e2e',
  textPrimary: '#e2e1f0',
  textSecondary: '#6b6880',
  textMuted: '#4a4858',
  orange: '#f97316',
  orangeHover: '#ea580c',
  success: '#22c55e',
  error: '#ef4444',
  warning: '#f59e0b',
};

// ─── Category config ──────────────────────────────────────────────────────────

type Category = 'papel' | 'tinta' | 'transfer' | 'embalaje' | 'otro';

const CATEGORIES: Category[] = ['papel', 'tinta', 'transfer', 'embalaje', 'otro'];

const CAT_COLORS: Record<Category, string> = {
  papel: '#3b82f6',
  tinta: '#8b5cf6',
  transfer: '#f97316',
  embalaje: '#22c55e',
  otro: '#6b7280',
};

const CAT_LABELS: Record<Category, string> = {
  papel: 'Papel',
  tinta: 'Tinta',
  transfer: 'Transfer',
  embalaje: 'Embalaje',
  otro: 'Otro',
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_STOCK: StockItem[] = [
  { id: 1, name: 'Papel A4 80g (Resma)', category: 'papel', unit: 'resma', quantity: 45, minQuantity: 10, costPerUnit: 3.50, isLowStock: false, createdAt: new Date().toISOString() },
  { id: 2, name: 'Papel A3 90g (Resma)', category: 'papel', unit: 'resma', quantity: 8, minQuantity: 10, costPerUnit: 5.20, isLowStock: true, createdAt: new Date().toISOString() },
  { id: 3, name: 'Tinta Cyan Epson 1L', category: 'tinta', unit: 'litro', quantity: 12, minQuantity: 5, costPerUnit: 18.00, isLowStock: false, createdAt: new Date().toISOString() },
  { id: 4, name: 'Tinta Magenta Epson 1L', category: 'tinta', unit: 'litro', quantity: 3, minQuantity: 5, costPerUnit: 18.00, isLowStock: true, createdAt: new Date().toISOString() },
  { id: 5, name: 'Tinta Amarilla Epson 1L', category: 'tinta', unit: 'litro', quantity: 2, minQuantity: 5, costPerUnit: 18.00, isLowStock: true, createdAt: new Date().toISOString() },
  { id: 6, name: 'Film Transfer PET 30cm', category: 'transfer', unit: 'metro', quantity: 350, minQuantity: 100, costPerUnit: 0.80, isLowStock: false, createdAt: new Date().toISOString() },
  { id: 7, name: 'Film Transfer PET 60cm', category: 'transfer', unit: 'metro', quantity: 85, minQuantity: 100, costPerUnit: 1.40, isLowStock: true, createdAt: new Date().toISOString() },
  { id: 8, name: 'Caja Envío Pequeña', category: 'embalaje', unit: 'unidad', quantity: 200, minQuantity: 50, costPerUnit: 0.35, isLowStock: false, createdAt: new Date().toISOString() },
  { id: 9, name: 'Caja Envío Grande', category: 'embalaje', unit: 'unidad', quantity: 42, minQuantity: 50, costPerUnit: 0.70, isLowStock: true, createdAt: new Date().toISOString() },
  { id: 10, name: 'Papel Burbuja (rollo)', category: 'embalaje', unit: 'rollo', quantity: 6, minQuantity: 3, costPerUnit: 12.00, isLowStock: false, createdAt: new Date().toISOString() },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0a0a0f', border: `1px solid ${C.border}`,
  borderRadius: 8, color: C.textPrimary, fontSize: 13, padding: '10px 12px',
  fontFamily: 'DM Sans, sans-serif', outline: 'none', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, color: C.textMuted, letterSpacing: '0.06em',
  fontFamily: 'JetBrains Mono, monospace', display: 'block', marginBottom: 6,
};

function formatCurrency(n?: number) {
  if (!n) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(n);
}

// ─── Stock bar ────────────────────────────────────────────────────────────────

function StockBar({ item }: { item: StockItem }) {
  const pct = item.minQuantity > 0 ? Math.min((item.quantity / (item.minQuantity * 3)) * 100, 100) : 100;
  const color = item.isLowStock ? C.error : item.quantity < item.minQuantity * 1.5 ? C.warning : C.success;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 6, background: '#1a1a2a', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, color, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, minWidth: 32, textAlign: 'right' }}>{item.quantity}</span>
    </div>
  );
}

// ─── Category badge ───────────────────────────────────────────────────────────

function CatBadge({ cat }: { cat: string }) {
  const color = CAT_COLORS[cat as Category] || CAT_COLORS.otro;
  const label = CAT_LABELS[cat as Category] || cat;
  return (
    <span style={{ padding: '3px 9px', borderRadius: 20, background: `${color}18`, border: `1px solid ${color}40`, color, fontSize: 11, fontWeight: 600, fontFamily: 'DM Sans, sans-serif', whiteSpace: 'nowrap' }}>
      {label}
    </span>
  );
}

// ─── Item modal (add / edit) ──────────────────────────────────────────────────

function StockModal({ item, onClose, onSaved, isMobile }: { item?: StockItem; onClose: () => void; onSaved: (it: StockItem) => void; isMobile?: boolean }) {
  const qc = useQueryClient();
  const isNew = !item;
  const [form, setForm] = useState<Partial<StockItem>>(item || { category: 'papel', unit: 'unidad', quantity: 0, minQuantity: 5 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.name?.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    setError(null);
    const payload = { ...form };
    try {
      let result: StockItem;
      if (isNew) {
        result = await stockApi.create(payload);
      } else {
        result = await stockApi.update(item!.id, payload);
      }
      qc.invalidateQueries({ queryKey: ['stock'] });
      onSaved(result);
    } catch {
      const saved: StockItem = {
        id: item?.id || Date.now(),
        name: form.name || '',
        category: form.category || 'otro',
        unit: form.unit || 'unidad',
        quantity: Number(form.quantity) || 0,
        minQuantity: Number(form.minQuantity) || 0,
        costPerUnit: Number(form.costPerUnit) || undefined,
        isLowStock: Number(form.quantity) < Number(form.minQuantity),
        createdAt: item?.createdAt || new Date().toISOString(),
      };
      onSaved(saved);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: isMobile ? 0 : '50%', left: isMobile ? 0 : '50%', transform: isMobile ? 'none' : 'translate(-50%, -50%)', zIndex: 201, width: isMobile ? '100%' : '90%', maxWidth: isMobile ? 'none' : 520, height: isMobile ? '100%' : 'auto', overflowY: isMobile ? 'auto' : undefined, background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? 0 : 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Package size={16} color={C.orange} />
          </div>
          <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 18, color: '#fff', margin: 0, flex: 1, letterSpacing: '-0.02em' }}>
            {isNew ? 'Añadir Item' : 'Editar Item'}
          </h2>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={labelStyle}>NOMBRE *</label>
            <input style={inputStyle} placeholder="Papel A4 80g" value={form.name || ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>CATEGORÍA</label>
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.category || 'otro'} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {CATEGORIES.map(c => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>UNIDAD</label>
            <input style={inputStyle} placeholder="resma, litro, metro..." value={form.unit || ''} onChange={e => setForm(f => ({ ...f, unit: e.target.value }))} />
          </div>
          <div>
            <label style={labelStyle}>CANTIDAD</label>
            <input type="number" min="0" style={inputStyle} value={form.quantity ?? ''} onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))} />
          </div>
          <div>
            <label style={labelStyle}>STOCK MÍNIMO</label>
            <input type="number" min="0" style={inputStyle} value={form.minQuantity ?? ''} onChange={e => setForm(f => ({ ...f, minQuantity: Number(e.target.value) }))} />
          </div>
          <div>
            <label style={labelStyle}>PRECIO UNIDAD (€)</label>
            <input type="number" min="0" step="0.01" style={inputStyle} placeholder="0.00" value={form.costPerUnit ?? ''} onChange={e => setForm(f => ({ ...f, costPerUnit: Number(e.target.value) || undefined }))} />
          </div>
        </div>

        {error && (
          <div style={{ margin: '0 24px 12px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: C.error }}>
            {error}
          </div>
        )}

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 9, border: 'none',
            background: saving ? '#2a2a3a' : `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`,
            color: saving ? C.textMuted : '#fff', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
            boxShadow: saving ? 'none' : '0 4px 14px rgba(249,115,22,0.35)',
          }}>
            {saving ? <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <Save size={14} />}
            {saving ? 'Guardando...' : isNew ? 'Añadir' : 'Guardar'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Inline quantity editor ───────────────────────────────────────────────────

function QtyEditor({ item, onSaved }: { item: StockItem; onSaved: (it: StockItem) => void }) {
  const [val, setVal] = useState(String(item.quantity));
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  const handleSave = async () => {
    const qty = Number(val);
    if (isNaN(qty) || qty < 0) return;
    setSaving(true);
    try {
      const result = await stockApi.update(item.id, { quantity: qty });
      qc.invalidateQueries({ queryKey: ['stock'] });
      onSaved({ ...item, quantity: qty, isLowStock: qty < item.minQuantity });
    } catch {
      onSaved({ ...item, quantity: qty, isLowStock: qty < item.minQuantity });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <input
        type="number"
        min="0"
        value={val}
        onChange={e => setVal(e.target.value)}
        style={{ width: 64, background: '#0a0a0f', border: `1px solid ${C.border}`, borderRadius: 6, color: C.textPrimary, fontSize: 12, padding: '5px 8px', fontFamily: 'JetBrains Mono, monospace', outline: 'none' }}
      />
      {val !== String(item.quantity) && (
        <button onClick={handleSave} disabled={saving} style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: `${C.success}22`, color: C.success, fontSize: 11, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
          {saving ? '...' : 'OK'}
        </button>
      )}
    </div>
  );
}

// ─── Stock entry modal ────────────────────────────────────────────────────────

function StockEntryModal({ item, onClose, onSaved, isMobile }: { item: StockItem; onClose: () => void; onSaved: (it: StockItem) => void; isMobile?: boolean }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<StockEntry>({ quantity: 1, supplier: '', date: today, notes: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = async () => {
    if (!form.quantity || form.quantity <= 0) { setError('La cantidad debe ser mayor que 0'); return; }
    setSaving(true);
    setError(null);
    const payload: StockEntry = {
      quantity: Number(form.quantity),
      supplier: form.supplier?.trim() || undefined,
      date: form.date || today,
      notes: form.notes?.trim() || undefined,
    };
    try {
      const result = await stockApi.addEntry(item.id, payload);
      qc.invalidateQueries({ queryKey: ['stock'] });
      onSaved(result);
    } catch {
      // Optimistic fallback: add quantity locally
      const updated: StockItem = {
        ...item,
        quantity: item.quantity + Number(form.quantity),
        isLowStock: (item.quantity + Number(form.quantity)) < item.minQuantity,
      };
      onSaved(updated);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', top: isMobile ? 0 : '50%', left: isMobile ? 0 : '50%', transform: isMobile ? 'none' : 'translate(-50%, -50%)', zIndex: 201, width: isMobile ? '100%' : '90%', maxWidth: isMobile ? 'none' : 480, height: isMobile ? '100%' : 'auto', overflowY: isMobile ? 'auto' : undefined, background: C.card, border: `1px solid ${C.border}`, borderRadius: isMobile ? 0 : 16, boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <PackagePlus size={16} color={C.success} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 17, color: '#fff', margin: 0, letterSpacing: '-0.02em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              Entrada de stock
            </h2>
            <p style={{ margin: 0, fontSize: 12, color: C.textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</p>
          </div>
          <button onClick={onClose} style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textSecondary }}>
            <X size={15} />
          </button>
        </div>

        <div style={{ padding: '24px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div>
            <label style={labelStyle}>CANTIDAD A AÑADIR *</label>
            <input
              type="number" min="1" autoFocus
              style={inputStyle}
              value={form.quantity}
              onChange={e => setForm(f => ({ ...f, quantity: Number(e.target.value) }))}
            />
          </div>
          <div>
            <label style={labelStyle}>FECHA DE ENTRADA</label>
            <input
              type="date"
              style={inputStyle}
              value={form.date}
              onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
            />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={labelStyle}>PROVEEDOR</label>
            <input
              style={inputStyle}
              placeholder="Nombre del proveedor (opcional)"
              value={form.supplier || ''}
              onChange={e => setForm(f => ({ ...f, supplier: e.target.value }))}
            />
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={labelStyle}>NOTAS</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
              placeholder="Observaciones (opcional)"
              value={form.notes || ''}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </div>

        {error && (
          <div style={{ margin: '0 24px 12px', padding: '10px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, fontSize: 12, color: C.error }}>
            {error}
          </div>
        )}

        <div style={{ padding: '16px 24px', borderTop: `1px solid ${C.border}`, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', borderRadius: 9, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 9, border: 'none',
            background: saving ? '#2a2a3a' : `linear-gradient(135deg, ${C.success}, #16a34a)`,
            color: saving ? C.textMuted : '#fff', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer',
            fontFamily: 'DM Sans, sans-serif', fontWeight: 600,
            boxShadow: saving ? 'none' : '0 4px 14px rgba(34,197,94,0.35)',
          }}>
            {saving ? <div style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} /> : <PackagePlus size={14} />}
            {saving ? 'Registrando...' : 'Registrar entrada'}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Main StockPage ───────────────────────────────────────────────────────────

export default function StockPage() {
  const [filterCat, setFilterCat] = useState<Category | 'all' | 'low'>('all');
  const [search, setSearch] = useState('');
  const [modalItem, setModalItem] = useState<StockItem | null | 'new'>(null);
  const [entryItem, setEntryItem] = useState<StockItem | null>(null);
  const [extraItems, setExtraItems] = useState<StockItem[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const { data: apiStock, isLoading } = useQuery({
    queryKey: ['stock'],
    queryFn: () => stockApi.list(),
    retry: false,
  });

  const baseItems: StockItem[] = (Array.isArray(apiStock) && apiStock.length > 0) ? apiStock : MOCK_STOCK;
  const allItems = useMemo(() => {
    const ids = new Set(baseItems.map(s => s.id));
    return [...baseItems, ...extraItems.filter(s => !ids.has(s.id))].map(s => ({
      ...s,
      isLowStock: s.quantity < s.minQuantity,
    }));
  }, [baseItems, extraItems]);

  const filtered = useMemo(() => {
    let list = [...allItems];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(s => s.name.toLowerCase().includes(q) || s.category.toLowerCase().includes(q));
    }
    if (filterCat === 'low') list = list.filter(s => s.isLowStock);
    else if (filterCat !== 'all') list = list.filter(s => s.category === filterCat);
    return list;
  }, [allItems, search, filterCat]);

  const totalValue = allItems.reduce((sum, s) => sum + (s.costPerUnit || 0) * s.quantity, 0);
  const lowCount = allItems.filter(s => s.isLowStock).length;

  const handleSaved = (it: StockItem) => {
    setExtraItems(prev => {
      const exists = prev.some(x => x.id === it.id);
      return exists ? prev.map(x => x.id === it.id ? it : x) : [...prev, it];
    });
    setModalItem(null);
  };

  const handleQtySaved = (it: StockItem) => {
    setExtraItems(prev => {
      const exists = prev.some(x => x.id === it.id);
      return exists ? prev.map(x => x.id === it.id ? it : x) : [...prev, it];
    });
  };

  const handleEntrySaved = (it: StockItem) => {
    setExtraItems(prev => {
      const exists = prev.some(x => x.id === it.id);
      return exists ? prev.map(x => x.id === it.id ? it : x) : [...prev, it];
    });
    setEntryItem(null);
  };

  return (
    <div style={{ padding: isMobile ? '20px 16px' : '32px', minHeight: '100vh', background: C.bg, fontFamily: 'DM Sans, sans-serif' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'flex-start', gap: isMobile ? 16 : 0, marginBottom: 28 }}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 900, fontSize: isMobile ? 24 : 28, color: '#fff', margin: '0 0 6px', letterSpacing: '-0.03em' }}>Stock</h1>
          <p style={{ fontSize: 13, color: C.textSecondary, margin: 0 }}>Control de inventario y materiales</p>
        </div>
        <button
          onClick={() => setModalItem('new')}
          style={{ display: 'flex', alignItems: 'center', gap: 8, background: `linear-gradient(135deg, ${C.orange}, ${C.orangeHover})`, border: 'none', borderRadius: 10, padding: '10px 18px', color: '#fff', fontSize: 13, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: 600, boxShadow: '0 4px 14px rgba(249,115,22,0.35)', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'center' : undefined }}
        >
          <Plus size={16} /> Añadir Item
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { icon: Package, label: 'Total Items', value: String(allItems.length), color: C.orange, bg: 'rgba(249,115,22,0.1)', border: 'rgba(249,115,22,0.25)' },
          { icon: AlertTriangle, label: 'Stock Bajo', value: String(lowCount), color: lowCount > 0 ? C.error : C.success, bg: lowCount > 0 ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)', border: lowCount > 0 ? 'rgba(239,68,68,0.25)' : 'rgba(34,197,94,0.25)' },
          { icon: DollarSign, label: 'Valor Total', value: formatCurrency(totalValue), color: '#a78bfa', bg: 'rgba(167,139,250,0.08)', border: 'rgba(167,139,250,0.25)' },
        ].map(({ icon: Icon, label, value, color, bg, border }) => (
          <div key={label} style={{ padding: '20px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 42, height: 42, borderRadius: 11, background: bg, border: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon size={18} color={color} />
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 800, color, fontFamily: 'Syne, sans-serif', letterSpacing: '-0.02em' }}>{value}</div>
                <div style={{ fontSize: 12, color: C.textSecondary }}>{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 10, marginBottom: 20, alignItems: isMobile ? 'stretch' : 'center' }}>
        {/* Search */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: isMobile ? undefined : '1 1 200px', background: C.card, border: `1px solid ${C.border}`, borderRadius: 9, padding: '9px 14px' }}>
          <Search size={14} color={C.textSecondary} />
          <input
            type="text"
            placeholder="Buscar materiales..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: 'transparent', border: 'none', outline: 'none', color: '#fff', fontSize: 13, width: '100%', fontFamily: 'DM Sans, sans-serif' }}
          />
          {search && <button onClick={() => setSearch('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex', padding: 0 }}><X size={13} /></button>}
        </div>

        {/* Category filters — scrollable on mobile */}
        <div style={{ display: 'flex', gap: 6, flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : undefined, paddingBottom: isMobile ? 4 : 0 }}>
          {([['all', 'Todos', C.textSecondary], ['low', 'Stock bajo', C.error], ...CATEGORIES.map(c => [c, CAT_LABELS[c], CAT_COLORS[c]])] as [string, string, string][]).map(([val, label, color]) => {
            const active = filterCat === val;
            return (
              <button
                key={val}
                onClick={() => setFilterCat(val as any)}
                style={{
                  padding: '7px 14px', borderRadius: 8, border: active ? `1px solid ${color}60` : `1px solid ${C.border}`,
                  background: active ? `${color}18` : C.card,
                  color: active ? color : C.textSecondary,
                  fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontWeight: active ? 600 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {val === 'low' && <AlertTriangle size={11} style={{ marginRight: 5, display: 'inline-block', verticalAlign: 'middle' }} />}
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table / Cards */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div style={{ width: 32, height: 32, border: `3px solid ${C.border}`, borderTopColor: C.orange, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
        </div>
      ) : isMobile ? (
        /* Mobile: card view */
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(item => (
            <div key={item.id} style={{ background: C.card, border: `1px solid ${item.isLowStock ? 'rgba(239,68,68,0.3)' : C.border}`, borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    {item.isLowStock && <AlertTriangle size={13} color={C.error} />}
                    <span style={{ fontSize: 13, fontWeight: 600, color: item.isLowStock ? C.error : C.textPrimary }}>{item.name}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CatBadge cat={item.category} />
                    <span style={{ fontSize: 11, color: C.textMuted }}>por {item.unit}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button
                    onClick={() => setEntryItem(item)}
                    title="Registrar entrada de stock"
                    style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: `1px solid rgba(34,197,94,0.35)`, background: 'rgba(34,197,94,0.08)', color: C.success, cursor: 'pointer', fontSize: 11, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}
                  >
                    <PackagePlus size={12} /> Entrada
                  </button>
                  <button onClick={() => setModalItem(item)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: 11 }}>
                    Editar
                  </button>
                </div>
              </div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: C.textMuted }}>Stock (mín: {item.minQuantity})</span>
                  <span style={{ fontSize: 11, color: C.textSecondary }}>{formatCurrency(item.costPerUnit)}/u</span>
                </div>
                <StockBar item={item} />
              </div>
              <QtyEditor item={item} onSaved={handleQtySaved} />
            </div>
          ))}
          {filtered.length === 0 && (
            <div style={{ padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
              No se encontraron items
            </div>
          )}
        </div>
      ) : (
        /* Desktop: table view */
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                {['Ítem', 'Categoría', 'Cantidad', 'Mín.', 'Precio/U', ''].map(h => (
                  <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, color: C.textMuted, fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr
                  key={item.id}
                  style={{ transition: 'background 0.1s', background: item.isLowStock ? 'rgba(239,68,68,0.03)' : 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = item.isLowStock ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.02)')}
                  onMouseLeave={e => (e.currentTarget.style.background = item.isLowStock ? 'rgba(239,68,68,0.03)' : 'transparent')}
                >
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {item.isLowStock && <AlertTriangle size={13} color={C.error} style={{ flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: item.isLowStock ? C.error : C.textPrimary }}>{item.name}</div>
                        <div style={{ fontSize: 11, color: C.textMuted }}>por {item.unit}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <CatBadge cat={item.category} />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28`, minWidth: 160 }}>
                    <StockBar item={item} />
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <span style={{ fontSize: 12, color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace' }}>{item.minQuantity}</span>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <span style={{ fontSize: 12, color: C.textSecondary, fontFamily: 'JetBrains Mono, monospace' }}>{formatCurrency(item.costPerUnit)}</span>
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: `1px solid #1a1a28` }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <QtyEditor item={item} onSaved={handleQtySaved} />
                      <button
                        onClick={() => setEntryItem(item)}
                        title="Registrar entrada de stock"
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: 6, border: `1px solid rgba(34,197,94,0.35)`, background: 'rgba(34,197,94,0.08)', color: C.success, cursor: 'pointer', fontSize: 11, fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}
                      >
                        <PackagePlus size={12} /> Entrada
                      </button>
                      <button onClick={() => setModalItem(item)} style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${C.border}`, background: 'transparent', color: C.textSecondary, cursor: 'pointer', fontSize: 11 }}>
                        Editar
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '48px 16px', textAlign: 'center', color: C.textMuted, fontSize: 14 }}>
                    No se encontraron items
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Item modal (add/edit) */}
      {modalItem !== null && (
        <StockModal
          item={modalItem === 'new' ? undefined : modalItem}
          onClose={() => setModalItem(null)}
          onSaved={handleSaved}
          isMobile={isMobile}
        />
      )}

      {/* Stock entry modal */}
      {entryItem !== null && (
        <StockEntryModal
          item={entryItem}
          onClose={() => setEntryItem(null)}
          onSaved={handleEntrySaved}
          isMobile={isMobile}
        />
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
